import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Alert,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { getUserById, listEvents, updateUser as updateUserSupabase } from '../services/database';

const UpgradeClubScreen = ({ navigation, route }) => {
  const { clubId } = route.params || {};
  const { user, appUser } = useAuth();
  const { showToast } = useToast();
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [eventCount, setEventCount] = useState(0);

  useEffect(() => {
    fetchClubData();
  }, [clubId]);

  const fetchClubData = async () => {
    try {
      setLoading(true);
      
      let club;
      if (clubId) {
        const raw = await getUserById(clubId);
        club = raw ? { id: raw.id, clubName: raw.club_name, tier: raw.tier, claimedBy: raw.claimed_by } : null;
      } else {
        club = appUser;
      }

      setClubData(club);
      
      if (club?.id) {
        fetchEventCountForClub(club.id);
      }
    } catch (error) {
      console.error('Error fetching club data:', error);
      showToast('Failed to load club information', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventCountForClub = async (id) => {
    try {
      const events = await listEvents({ clubId: id });
      setEventCount((events || []).length);
    } catch (error) {
      console.error('Error fetching event count:', error);
    }
  };

  const handleUpgrade = async () => {
    if (!clubData) return;

    const confirmUpgrade = Platform.OS === 'web'
      ? window.confirm('Upgrade to Pro tier for $29/month? This will unlock unlimited events and paid event features.')
      : await new Promise((resolve) => {
          Alert.alert(
            'Upgrade to Pro',
            'Upgrade to Pro tier for $29/month? This will unlock unlimited events and paid event features.',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Upgrade', style: 'default', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmUpgrade) return;

    setUpgrading(true);
    try {
      // TODO: Integrate with Stripe for subscription payment
      // For now, we'll just update the tier directly
      // In production, this should:
      // 1. Create Stripe subscription
      // 2. Wait for payment confirmation
      // 3. Then update tier

      await updateUserSupabase(clubData.id, { tier: 'PRO' });

      showToast('Successfully upgraded to Pro tier!', 'success');
      navigation.goBack();
    } catch (error) {
      console.error('Error upgrading club:', error);
      showToast('Failed to upgrade. Please try again.', 'error');
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  if (!clubData) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>Club not found</Text>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const isPro = clubData.tier === 'PRO';
  const isClaimed = !!clubData.claimedBy;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color="#333" />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Upgrade Club</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Club Info */}
        <View style={styles.clubInfoCard}>
          <Text style={styles.clubName}>{clubData.clubName || 'Your Club'}</Text>
          <View style={styles.tierBadge}>
            <Text style={styles.tierText}>
              Current: {isPro ? 'Pro' : 'Free'} Tier
            </Text>
          </View>
        </View>

        {/* Current Usage */}
        {!isPro && (
          <View style={styles.usageCard}>
            <Text style={styles.usageTitle}>Current Usage</Text>
            <View style={styles.usageItem}>
              <Text style={styles.usageLabel}>Events Created</Text>
              <Text style={styles.usageValue}>{eventCount} / 3</Text>
            </View>
            <View style={styles.progressBar}>
              <View 
                style={[styles.progressFill, { width: `${Math.min((eventCount / 3) * 100, 100)}%` }]} 
              />
            </View>
            {eventCount >= 3 && (
              <Text style={styles.limitReachedText}>
                ⚠️ You've reached the free tier limit
              </Text>
            )}
          </View>
        )}

        {/* Tier Comparison */}
        <View style={styles.comparisonCard}>
          <Text style={styles.comparisonTitle}>Tier Comparison</Text>
          
          <View style={styles.featureRow}>
            <Text style={styles.featureName}>Free Events</Text>
            <View style={styles.featureValues}>
              <Text style={styles.featureValue}>3</Text>
              <Text style={styles.featureValuePro}>Unlimited</Text>
            </View>
          </View>

          <View style={styles.featureRow}>
            <Text style={styles.featureName}>Paid Events</Text>
            <View style={styles.featureValues}>
              <Icon name="close" size={20} color="#999" />
              <Icon name="check" size={20} color="#94EB98" />
            </View>
          </View>

          <View style={styles.featureRow}>
            <Text style={styles.featureName}>Payment Processing</Text>
            <View style={styles.featureValues}>
              <Icon name="close" size={20} color="#999" />
              <Icon name="check" size={20} color="#94EB98" />
            </View>
          </View>

          <View style={styles.featureRow}>
            <Text style={styles.featureName}>Advanced Analytics</Text>
            <View style={styles.featureValues}>
              <Icon name="close" size={20} color="#999" />
              <Icon name="check" size={20} color="#94EB98" />
            </View>
          </View>

          <View style={styles.featureRow}>
            <Text style={styles.featureName}>Priority Support</Text>
            <View style={styles.featureValues}>
              <Icon name="close" size={20} color="#999" />
              <Icon name="check" size={20} color="#94EB98" />
            </View>
          </View>
        </View>

        {/* Pricing */}
        <View style={styles.pricingCard}>
          <View style={styles.pricingHeader}>
            <Text style={styles.pricingTitle}>Pro Tier</Text>
            <Text style={styles.pricingAmount}>$29</Text>
            <Text style={styles.pricingPeriod}>per month</Text>
          </View>
          <Text style={styles.pricingDescription}>
            Unlock unlimited events, paid event features, and advanced tools to grow your chess club.
          </Text>
        </View>

        {/* Upgrade Button */}
        {!isPro && (
          <TouchableOpacity
            style={[styles.upgradeButton, upgrading && styles.upgradeButtonDisabled]}
            onPress={handleUpgrade}
            disabled={upgrading || !isClaimed}
            activeOpacity={0.8}
          >
            {upgrading ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <>
                <Icon name="star" size={20} color="white" />
                <Text style={styles.upgradeButtonText}>
                  {!isClaimed ? 'Claim Your Club First' : 'Upgrade to Pro'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        )}

        {isPro && (
          <View style={styles.proBadge}>
            <Icon name="check-circle" size={24} color="#94EB98" />
            <Text style={styles.proBadgeText}>You're on Pro tier!</Text>
          </View>
        )}

        {!isClaimed && (
          <TouchableOpacity
            style={styles.claimButton}
            onPress={() => {
              if (clubId) {
                navigation.navigate('UserDetail', { userId: clubId });
              } else {
                navigation.navigate('Profile');
              }
            }}
          >
            <Text style={styles.claimButtonText}>Claim Your Club First</Text>
          </TouchableOpacity>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  errorText: {
    fontSize: 18,
    color: '#999',
    marginBottom: 20,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: 'white',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  backText: {
    fontSize: 16,
    color: '#333',
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  clubInfoCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    alignItems: 'center',
  },
  clubName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  tierBadge: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  tierText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2E7D32',
  },
  usageCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  },
  usageTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  usageItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  usageLabel: {
    fontSize: 16,
    color: '#666',
  },
  usageValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  progressBar: {
    height: 8,
    backgroundColor: '#e0e0e0',
    borderRadius: 4,
    marginTop: 12,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#94EB98',
    borderRadius: 4,
  },
  limitReachedText: {
    fontSize: 14,
    color: '#FF9800',
    marginTop: 8,
    fontWeight: '600',
  },
  comparisonCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  },
  comparisonTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  featureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  featureName: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  featureValues: {
    flexDirection: 'row',
    gap: 40,
  },
  featureValue: {
    fontSize: 16,
    color: '#666',
    width: 60,
    textAlign: 'center',
  },
  featureValuePro: {
    fontSize: 16,
    fontWeight: '600',
    color: '#94EB98',
    width: 60,
    textAlign: 'center',
  },
  pricingCard: {
    backgroundColor: '#E8F5E9',
    borderRadius: 12,
    padding: 24,
    marginBottom: 16,
    alignItems: 'center',
  },
  pricingHeader: {
    alignItems: 'center',
    marginBottom: 12,
  },
  pricingTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  pricingAmount: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#2E7D32',
  },
  pricingPeriod: {
    fontSize: 16,
    color: '#666',
    marginTop: 4,
  },
  pricingDescription: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
  },
  upgradeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#94EB98',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  upgradeButtonDisabled: {
    opacity: 0.6,
  },
  upgradeButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
  },
  proBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#E8F5E9',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  proBadgeText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2E7D32',
  },
  claimButton: {
    backgroundColor: '#FF9800',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  claimButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  backButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: '#94EB98',
    borderRadius: 8,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
});

export default UpgradeClubScreen;

